import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaUserPlus, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const AssetAssignmentModal = ({ show, onHide, asset, users, onAssign }) => {
    const { t } = useTranslation('assets');

    const [formData, setFormData] = useState({
        user_id: '',
        delivery_type: 'Assignment',
        device_condition: 'Good',
        date_assigned: new Date().toISOString().split('T')[0],
        delivered_by: '',
        recipient_signature: '',
        head_of_ict_full_name: '',
        head_ict_signature: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (show) {
            setFormData({
                user_id: '',
                delivery_type: 'Assignment',
                device_condition: 'Good',
                date_assigned: new Date().toISOString().split('T')[0],
                delivered_by: '',
                recipient_signature: '',
                head_of_ict_full_name: '',
                head_ict_signature: ''
            });
            setErrors({});
        }
    }, [show]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.user_id) {
            newErrors.user_id = t('assignment.validationUserRequired');
        }
        if (!formData.delivery_type) {
            newErrors.delivery_type = t('assignment.validationDeliveryTypeRequired');
        }
        if (!formData.device_condition) {
            newErrors.device_condition = t('assignment.validationDeviceConditionRequired');
        }
        if (!formData.date_assigned) {
            newErrors.date_assigned = t('assignment.validationDateRequired');
        }
        if (!formData.delivered_by.trim()) {
            newErrors.delivered_by = t('assignment.validationDeliveredByRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            await onAssign(formData);
        } catch (error) {
            console.error(t('assignmentError'), error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            user_id: '',
            delivery_type: 'Assignment',
            device_condition: 'Good',
            date_assigned: new Date().toISOString().split('T')[0],
            delivered_by: '',
            recipient_signature: '',
            head_of_ict_full_name: '',
            head_ict_signature: ''
        });
        setErrors({});
        onHide();
    };

    // Filter active users only
    const activeUsers = users.filter(user => user.is_active);

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            size="lg"
            backdrop="static"
            centered
        >
            <Modal.Header className="bg-primary text-white">
                <Modal.Title className="d-flex align-items-center">
                    <FaUserPlus className="me-2" />
                    {t('assignment.title')}
                </Modal.Title>
                <Button
                    variant="link"
                    className="text-white p-0 border-0"
                    onClick={handleCancel}
                >
                    <FaTimes size={20} />
                </Button>
            </Modal.Header>

            <Modal.Body>
                {asset && (
                    <Alert variant="info" className="mb-4">
                        <h6 className="mb-2">{t('assignment.assetDetails')}</h6>
                        <div className="row">
                            <div className="col-md-6">
                                <strong>{t('assets.assetType')}:</strong> {asset.asset_type}<br />
                                <strong>{t('assets.brand')}:</strong> {asset.brand}<br />
                                <strong>{t('assets.model')}:</strong> {asset.model}
                            </div>
                            <div className="col-md-6">
                                <strong>{t('assets.serialNumber')}:</strong> {asset.serial_number}<br />
                                <strong>{t('assignment.currentStatus')}:</strong> {asset.status}<br />
                                <strong>{t('assets.location')}:</strong> {asset.location || 'N/A'}
                            </div>
                        </div>
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>
                                    {t('assignment.assignToUser')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.user_id}
                                    required
                                >
                                    <option value="">{t('assignment.selectUser')}</option>
                                    {activeUsers && activeUsers.length > 0 ? (
                                        activeUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.first_name} {user.last_name} ({user.email})
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>{t('assignment.noActiveUsers')}</option>
                                    )}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.user_id}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>
                                    {t('assignment.deliveryType')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="delivery_type"
                                    value={formData.delivery_type}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.delivery_type}
                                    required
                                >
                                    <option value="Assignment">{t('assignment.assignment')}</option>
                                    <option value="Loan">{t('assignment.loan')}</option>
                                    <option value="Temporary">{t('assignment.temporary')}</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.delivery_type}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>
                                    {t('assignment.deviceCondition')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    name="device_condition"
                                    value={formData.device_condition}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.device_condition}
                                    required
                                >
                                    <option value="New">{t('assignment.new')}</option>
                                    <option value="Good">{t('assignment.good')}</option>
                                    <option value="Fair">{t('assignment.fair')}</option>
                                    <option value="Poor">{t('assignment.poor')}</option>
                                    <option value="Refurbished">{t('assignment.refurbished')}</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.device_condition}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>
                                    {t('assignment.dateAssigned')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date_assigned"
                                    value={formData.date_assigned}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.date_assigned}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.date_assigned}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>
                                    {t('assignment.deliveredBy')} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="delivered_by"
                                    value={formData.delivered_by}
                                    onChange={handleInputChange}
                                    placeholder={t('assignment.deliveredByPlaceholder')}
                                    isInvalid={!!errors.delivered_by}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.delivered_by}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t('assignment.recipientSignature')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="recipient_signature"
                                    value={formData.recipient_signature}
                                    onChange={handleInputChange}
                                    placeholder={t('assignment.recipientSignaturePlaceholder')}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t('assignment.headOfIctFullName')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="head_of_ict_full_name"
                                    value={formData.head_of_ict_full_name}
                                    onChange={handleInputChange}
                                    placeholder={t('assignment.headOfIctPlaceholder')}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>{t('assignment.headIctSignature')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="head_ict_signature"
                                    value={formData.head_ict_signature}
                                    onChange={handleInputChange}
                                    placeholder={t('assignment.headIctSignaturePlaceholder')}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.user_id || !formData.delivered_by.trim()}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            {t('assignment.assigning')}
                        </>
                    ) : (
                        <>
                            <FaUserPlus className="me-2" />
                            {t('assignment.assignAsset')}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssetAssignmentModal;